import { MatPasswordStrengthComponent } from '@angular-material-extensions/password-strength';
import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { User, UserCredential } from 'firebase/auth';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ILegalityDialogParams,
  ILegalityDialogResult,
  LegalityDialogComponent,
} from '../legality-dialog/legality-dialog.component';
import { ThemePalette } from '@angular/material/core';
import { NGX_AUTH_FIREBASEUI_ANIMATIONS } from '../animations';
import { AuthProcessService, AuthProvider } from '../auth-process.service';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import {
  INgAuthConfig,
  NG_AUTH_CONFIG_TOKEN,
} from '../ngx-auth-firebaseui-old/module-config';
import { EMAIL_REGEX, MaterialButtonType } from '../auth-interfaces';

@Component({
  selector: 'pt-auth-firebaseui',
  templateUrl: 'auth.component.html',
  styleUrls: ['auth.component.scss'],
  animations: NGX_AUTH_FIREBASEUI_ANIMATIONS,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [AuthProcessService],
})
export class AuthComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  private _onDestroy$ = new Subject<void>();

  @ViewChild(MatTabGroup, { static: false }) matTabGroup: MatTabGroup;
  @ViewChild(MatPasswordStrengthComponent, { static: false })
  passwordStrength: MatPasswordStrengthComponent;

  isLoading: boolean;
  //  google, facebook, twitter, github as array or all as one single string
  @Input() providers: AuthProvider[] | AuthProvider = AuthProvider.ALL;
  @Input() providersTheme: MaterialButtonType; // Classic, Stroked, etc.

  @Input() appearance: MatFormFieldAppearance;
  @Input() tabIndex: number | null;
  @Input() registrationEnabled = true;
  @Input() resetPasswordEnabled = true;
  @Input() guestEnabled = true;
  @Input() tosUrl: string;
  @Input() privacyPolicyUrl: string;
  @Input() goBackURL: string;
  @Input() messageOnAuthSuccess: string;
  @Input() messageOnAuthError: string;
  @Input() messageOnEmailConfirmationSuccess: string;

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onSuccess: EventEmitter<UserCredential | User>;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onError: EventEmitter<Error>;
  @Output() selectedTabChange = new EventEmitter<MatTabChangeEvent>();

  @Input() enableLengthRule = true;
  @Input() enableLowerCaseLetterRule = true;
  @Input() enableUpperCaseLetterRule = true;
  @Input() enableDigitRule = true;
  @Input() enableSpecialCharRule = true;
  @Input() min?: number;
  @Input() max?: number;
  @Input() customValidator: RegExp;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onStrengthChanged = new EventEmitter<number>();

  // Verify email template to use in place of default template.
  // See email-confirmation component
  @Input() verifyEmailTemplate: TemplateRef<unknown>;

  // i18n translations to use in default template for email verification.
  // See email-confirmation component
  @Input() verifyEmailTitleText: string;
  @Input() verifyEmailConfirmationText: string;
  @Input() verifyEmailGoBackText: string;
  @Input() sendNewVerificationEmailText: string;
  @Input() signOutText = 'Sign out';

  // Customize the text
  // Reset Password Tab
  @Input() resetPasswordTabText = 'Reset e-mail address to password';
  @Input() resetPasswordInputText = 'Reset e-mail address to password';
  @Input() resetPasswordErrorRequiredText =
    'E-mail is required to reset the password!';
  @Input() resetPasswordErrorPatternText =
    'Please enter a valid e-mail address';
  @Input() resetPasswordActionButtonText = 'Reset';
  @Input() resetPasswordInstructionsText =
    'Reset requested. Check your e-mail instructions.';

  // SignIn Tab
  @Input() signInTabText = 'Sign in';
  @Input() signInCardTitleText = 'Signing in';
  @Input() loginButtonText = 'Log In';
  @Input() forgotPasswordButtonText = 'Forgot Password ?';

  // Common
  @Input() nameText = 'Name';
  @Input() nameErrorRequiredText = 'Name is required';
  @Input() nameErrorMinLengthText = 'The name is too short!';
  @Input() nameErrorMaxLengthText = 'The name is too long!';

  @Input() emailText = 'E-mail';
  @Input() emailErrorRequiredText = 'E-mail is required';
  @Input() emailErrorPatternText = 'Please enter a valid e-mail address';

  @Input() passwordText = 'Password';
  @Input() passwordErrorRequiredText = 'Password is required';
  @Input() passwordErrorMinLengthText = 'The password is too short!';
  @Input() passwordErrorMaxLengthText = 'The password is too long!';

  // Register Tab
  @Input() registerTabText = 'Register';
  @Input() registerCardTitleText = 'Registration';
  @Input() registerButtonText = 'Register';
  @Input() guestButtonText = 'continue as guest';

  // email confirmation component
  @Input() emailConfirmationTitle = 'Confirm your e-mail address!';
  // eslint-disable-next-line max-len
  @Input()
  emailConfirmationText =
    `A confirmation e-mail has been sent to you. Check your inbox and click on the link "Confirm my e-mail" to confirm your e-mail address.`;

  authProvider = AuthProvider;
  passwordResetWished: boolean;

  public signInFormGroup: TypedFormGroup<{ email: string; password: string }>;
  public signUpFormGroup: TypedFormGroup<{
    name: string;
    email: string;
    password: string;
  }>;
  public resetPasswordFormGroup: TypedFormGroup<{ email: string }>;

  authenticationError = false;

  passReset = false;
  dialogRef?: MatDialogRef<LegalityDialogComponent, ILegalityDialogResult>;

  authProviders = AuthProvider;

  signInEmailFormControl: TypedFormControl<string>;
  sigInPasswordFormControl: TypedFormControl<string>;

  sigUpNameFormControl: TypedFormControl<string>;
  sigUpEmailFormControl: TypedFormControl<string>;
  sigUpPasswordFormControl: TypedFormControl<string>;
  resetPasswordEmailFormControl: TypedFormControl<string>;

  constructor(
    @Inject(PLATFORM_ID) private _platformId: object,
    @Inject(forwardRef(() => NG_AUTH_CONFIG_TOKEN))
    public config: INgAuthConfig,
    public auth: Auth,
    public authProcess: AuthProcessService,
    public dialog: MatDialog,
    private _activatedRoute: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef
  ) {
    this.onSuccess = authProcess.onSuccessEmitter;
    this.onError = authProcess.onErrorEmitter;
  }

  get color(): ThemePalette {
    return this.authenticationError ? 'warn' : 'primary';
  }

  public ngOnInit(): void {
    if (isPlatformBrowser(this._platformId)) {
      this.onError
        .pipe(takeUntil(this._onDestroy$))
        .subscribe(() => (this.authenticationError = true));
    }
    this.min =
      this.min !== undefined
        ? Math.max(this.min, this.config.passwordMinLength || 6)
        : this.config.passwordMinLength;
    this.max =
      this.max !== undefined
        ? Math.min(this.max, this.config.passwordMaxLength || 25)
        : this.config.passwordMaxLength;

    this.goBackURL = this._chooseBackUrl();

    this.updateAuthSnackbarMessages();
    // auth form's initialization
    this._initSignInFormGroupBuilder();
    this._initSignUpFormGroupBuilder();
    this._initResetPasswordFormGroupBuilder();
  }

  ngAfterViewInit(): void {
    if (this.passwordStrength) {
      this.passwordStrength.onStrengthChanged
        .pipe(takeUntil(this._onDestroy$))
        .subscribe((strength: number) => {
          this.onStrengthChanged.emit(strength);
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.messageOnAuthSuccess || changes.messageOnAuthError) {
      this.updateAuthSnackbarMessages();
    }
    if (changes.min) {
      this.min =
        this.min !== undefined
          ? Math.max(this.min, this.config.passwordMinLength || 6)
          : this.config.passwordMinLength;
    }
    if (changes.max) {
      this.max =
        this.max !== undefined
          ? Math.min(this.max, this.config.passwordMaxLength || 25)
          : this.config.passwordMaxLength;
    }
    if (changes.goBackURL) {
      this.goBackURL = this._chooseBackUrl();
    }
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.selectedTabChange.emit(event);
    this.tabIndex = event.index;
  }

  async signOut(): Promise<void> {
    try {
      this.isLoading = true;
      this._changeDetectorRef.markForCheck();
      await this.authProcess.signOut();
    } finally {
      this.isLoading = false;
      this.tabIndex = 0;
      this._changeDetectorRef.markForCheck();
    }
  }

  async signIn(): Promise<void> {
    if (!this.signInFormGroup.valid) {
      return;
    }
    try {
      this.isLoading = true;
      this._changeDetectorRef.markForCheck();
      await this.authProcess.signInWith(this.authProviders.EmailAndPassword, {
        email: this.signInFormGroup.value.email,
        password: this.signInFormGroup.value.password,
      });
    } finally {
      this.isLoading = false;
      this._changeDetectorRef.markForCheck();
    }
  }

  updateAuthSnackbarMessages(): void {
    this.authProcess.messageOnAuthSuccess = this.messageOnAuthSuccess;
    this.authProcess.messageOnAuthError = this.messageOnAuthError;
  }

  createForgotPasswordTab(): void {
    this.passwordResetWished = true;
    this.tabIndex = 2;
    this._changeDetectorRef.markForCheck();
  }

  async processLegalSignUP(authProvider: AuthProvider): Promise<void> {
    if (!this.tosUrl && !this.privacyPolicyUrl) {
      await this._afterSignUpMiddleware(authProvider);
      this.signUpFormGroup.reset();
      return;
    }

    const params: ILegalityDialogParams = {
      tosUrl: this.tosUrl,
      privacyPolicyUrl: this.privacyPolicyUrl,
      authProvider,
    };

    this.dialogRef = this.dialog.open<
      LegalityDialogComponent,
      ILegalityDialogParams,
      ILegalityDialogResult
    >(LegalityDialogComponent, {
      data: params,
    });

    if (!this.dialogRef) {
      return;
    }

    const result = await this.dialogRef.afterClosed().toPromise();

    if (result && result.checked) {
      await this._afterSignUpMiddleware(result.authProvider);
      this.signUpFormGroup.reset();
    }
    this.dialogRef = undefined;
  }

  async signUp(): Promise<void> {
    try {
      this.isLoading = true;
      this._changeDetectorRef.markForCheck();
      return await this.authProcess.signUp(this.signUpFormGroup.value.name, {
        email: this.signUpFormGroup.value.email,
        password: this.signUpFormGroup.value.password,
      });
    } finally {
      this.isLoading = false;
      this._changeDetectorRef.markForCheck();
    }
  }

  async signUpAnonymously(): Promise<void> {
    try {
      this.isLoading = true;
      this._changeDetectorRef.markForCheck();
      await this.authProcess.signInWith(this.authProvider.ANONYMOUS);
    } finally {
      this.isLoading = false;
      this._changeDetectorRef.markForCheck();
    }
  }

  async resetPassword(): Promise<void> {
    await this.authProcess.resetPassword(
      this.resetPasswordEmailFormControl.value
    );

    this.passReset = true;
    // this.tabIndex = 2;
    this._changeDetectorRef.markForCheck();
  }

  private _chooseBackUrl(): string {
    return (
      String(this._activatedRoute.snapshot.queryParams.redirectUrl) ||
      this.goBackURL ||
      '/'
    );
  }

  private _initSignInFormGroupBuilder(): void {
    this.signInEmailFormControl = new TypedFormControl('', [
      Validators.required,
      Validators.pattern(EMAIL_REGEX),
    ]);

    this.sigInPasswordFormControl = new TypedFormControl('', [
      Validators.required,
      Validators.minLength(this.min || 6),
      Validators.maxLength(this.max || 25),
    ]);

    this.signInFormGroup = new TypedFormGroup({
      email: this.signInEmailFormControl,
      password: this.sigInPasswordFormControl,
    });
  }

  private _initSignUpFormGroupBuilder(): void {
    this.sigUpNameFormControl = new TypedFormControl('', [
      Validators.required,
      Validators.minLength(this.config.nameMinLength ?? 0),
      Validators.maxLength(this.config.nameMaxLength ?? 255),
    ]);

    this.sigUpEmailFormControl = new TypedFormControl('', [
      Validators.required,
      Validators.pattern(EMAIL_REGEX),
    ]);

    this.sigUpPasswordFormControl = new TypedFormControl('', [
      Validators.required,
      Validators.minLength(this.min || 6),
      Validators.maxLength(this.max || 25),
    ]);

    this.signUpFormGroup = new TypedFormGroup({
      name: this.sigUpNameFormControl,
      email: this.sigUpEmailFormControl,
      password: this.sigUpPasswordFormControl,
    });
  }

  private _initResetPasswordFormGroupBuilder(): void {
    this.resetPasswordFormGroup = new TypedFormGroup({
      email: (this.resetPasswordEmailFormControl = new TypedFormControl('', [
        Validators.required,
        Validators.pattern(EMAIL_REGEX),
      ])),
    });
  }

  private async _afterSignUpMiddleware(
    authProvider?: AuthProvider
  ): Promise<void> {
    if (authProvider === this.authProvider.ANONYMOUS) {
      await this.signUpAnonymously();
    }
    await this.signUp();
  }
}
